/**
 * Публичная страница статуса сервиса без входа (§П5.5).
 */

import { formatDateTime, type LocaleCode } from "@hive/i18n";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "../api/client.js";
import { fetchServiceStatus, type ServiceStatusView } from "../api/support.js";

export interface StatusPageProps {
  readonly locale: LocaleCode;
}

export function StatusPage({ locale }: StatusPageProps): React.JSX.Element {
  const { t } = useTranslation(["support", "common"]);
  const [status, setStatus] = useState<ServiceStatusView | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    void fetchServiceStatus(controller.signal)
      .then(setStatus)
      .catch((err: unknown) => {
        setErrorCode(err instanceof ApiError ? err.code : "network.offline");
      });
    return () => controller.abort();
  }, []);

  const renderState = (state: string): string =>
    t(`support:status.state.${state}`, { defaultValue: state });

  return (
    <div className="status-page">
      <header className="status-page__head">
        <h1 className="status-page__title">{t("support:status.title")}</h1>
        <a className="button" href="/">
          {t("support:status.back")}
        </a>
      </header>

      {errorCode && (
        <p className="status-page__error" role="alert">
          {t(`errors:${errorCode}`, { defaultValue: errorCode })}
        </p>
      )}

      {status && (
        <>
          <p className="status-page__meta">
            {t("support:status.updated", {
              time: formatDateTime(locale, new Date(status.updatedAt), "UTC"),
            })}
          </p>
          <p className="status-page__meta">
            {t("support:status.version", { version: status.version })}
          </p>

          <ul className="status-page__components">
            {(["api", "database", "accrual"] as const).map((key) => (
              <li key={key} className="status-page__component">
                <span>{t(`support:status.component.${key}`)}</span>
                <span className={`status-page__badge status-page__badge--${status.components[key]}`}>
                  {renderState(status.components[key])}
                </span>
              </li>
            ))}
          </ul>

          <section className="status-page__incidents">
            <h2 className="status-page__section">{t("support:status.incidents.title")}</h2>
            {status.incidents.length === 0 ? (
              <p>{t("support:status.incidents.empty")}</p>
            ) : (
              <ul>
                {status.incidents.map((incident) => (
                  <li key={incident.id}>
                    <span>{t(`support:status.incidents.severity.${incident.severity}`)}</span>
                    <span>{incident.code}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}
